import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import usePortalFeatures from "../hooks/usePortalFeatures";
import { useAccess } from "../context/AccessContext";
import { buildDashboardModules, HIDE_INBOX_REVIEW } from "../config/portalFeatureNav";

export default function Dashboard() {
  const navigate = useNavigate();
  const { features, loading: featuresLoading } = usePortalFeatures();
  const { roles, loading: accessLoading } = useAccess();
  const roleError = !accessLoading && roles.length === 0;

  const modules = useMemo(() => {
    if (featuresLoading) return [];
    const items = buildDashboardModules(features);
    return HIDE_INBOX_REVIEW
      ? items.filter((item) => item.key !== "DOC_INDEX_HUB")
      : items;
  }, [features, featuresLoading]);

  const loading = featuresLoading || accessLoading;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Welcome</h1>
        <p style={styles.subtitle}>Quick access to your portal tools.</p>

        <div style={styles.roleBadge}>
          <span style={{ fontWeight: 600 }}>Roles:</span>

          {roles.length > 0 ? (
            roles.map((r, i) => (
              <span key={i} style={styles.role}>
                {r}
              </span>
            ))
          ) : (
            <span style={styles.role}>
              {accessLoading ? "Loading..." : "No Role Assigned"}
            </span>
          )}
        </div>
      </div>

      {roleError && (
        <div style={styles.errorBox}>
          ❌ You do not have any role assigned.
          <br />
          Please contact administrator.
        </div>
      )}

      {loading ? (
        <div style={{ padding: "20px" }}>Loading modules...</div>
      ) : roleError ? null : modules.length === 0 ? (
        <div style={styles.emptyBox}>⚠️ No accessible modules available.</div>
      ) : (
        <div style={styles.grid}>
          {modules.map((module) => {
            const Icon = module.icon;

            return (
              <div
                key={module.key}
                style={styles.card}
                onClick={() => {
                  if (window.location.pathname !== module.path) {
                    navigate(module.path);
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 20px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={styles.iconWrapper}>
                  <Icon size={22} />
                </div>

                <div>
                  <h3 style={styles.cardTitle}>{module.title}</h3>
                  <p style={styles.cardDesc}>{module.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "10px",
    background: "#f8fafc",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    minHeight: "80vh",
  },
  header: {
    marginBottom: "24px",
  },
  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "600",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  subtitle: {
    marginTop: "6px",
    color: "#64748b",
    fontSize: "14px",
  },
  roleBadge: {
    marginTop: "12px",
    fontSize: "13px",
    color: "#475569",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px",
  },
  role: {
    background: "#e0f2fe",
    color: "#0369a1",
    padding: "5px 12px",
    borderRadius: "20px",
    fontWeight: "500",
    whiteSpace: "nowrap",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  emptyBox: {
    background: "#fef9c3",
    color: "#92400e",
    padding: "12px",
    borderRadius: "8px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))",
    gap: "18px",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  card: {
    display: "flex",
    gap: "14px",
    alignItems: "center",
    padding: "18px",
    borderRadius: "12px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: 0,
    boxSizing: "border-box",
  },
  iconWrapper: {
    background: "#f1f5f9",
    padding: "10px",
    borderRadius: "10px",
    display: "flex",
  },
  cardTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600",
  },
  cardDesc: {
    margin: "4px 0 0",
    fontSize: "13px",
    color: "#64748b",
  },
};
